// "Kickoff pack" on a technical view (guide 21.7): the agenda PDF and a
// DRAFT email built from the view (back/utils/meetingPack). Nothing is sent
// from here - the email is shown to copy, edit and send yourself.
//
//   <MeetingPackButton projectId={projectId} view={{ viewId, version }} />
//
// Owner company only: for anyone else the server refuses and the button
// is not shown.
import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Space, Tooltip, message } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

export default function MeetingPackButton({ projectId, view }) {
  const viewId = view && view.viewId;
  const version = view && view.version;
  const qs = version != null ? `?version=${encodeURIComponent(version)}` : "";
  const base = `${url}/projects/${projectId}/meeting-pack/${encodeURIComponent(viewId || "")}`;
  const [allowed, setAllowed] = useState(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Probe once, so people who cannot build a pack never see the button.
  useEffect(() => {
    if (!projectId || !viewId) return undefined;
    let alive = true;
    setAllowed(null); setData(null);
    fetch(`${base}${qs}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null))
      // Only a complete answer shows the button, so a partial one can never break the page.
      .then((d) => { const ok = !!(d && d.email && d.pack); if (alive) { setAllowed(ok); setData(ok ? d : null); } })
      .catch(() => { if (alive) setAllowed(false); });
    return () => { alive = false; };
  }, [projectId, viewId, version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return null;

  const download = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${base}/pack.pdf${qs}`, { headers: auth() });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.err || `HTTP ${r.status}`); }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (((r.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || "kickoff-pack.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  // Filed in Document Control only when a person saves it (typed 'generated').
  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${base}/save${qs}`, { method: "POST", headers: auth() });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      message.success(b.duplicate ? "Already in Document Control" : b.revised ? `Saved to Document Control as revision ${b.doc.revisionNumber}` : "Saved to Document Control");
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const copy = async () => {
    const e = (data && data.email) || { subject: "", body: "" };
    try { await navigator.clipboard.writeText(`Subject: ${e.subject}\n\n${e.body}`); message.success("Email copied"); }
    catch (x) { message.info("Select the text and copy it"); }
  };

  const open_ = data && data.pack ? (data.pack.decisions || []).filter((d) => d.status !== "confirmed").length : 0;
  return (
    <>
      <Tooltip title="Agenda and a draft email for the kickoff, built from this view">
        <Button size="small" icon={<FileTextOutlined />} onClick={() => setOpen(true)}>Kickoff pack</Button>
      </Tooltip>
      <Modal open={open} onCancel={() => setOpen(false)} width={760} title="Kickoff meeting pack"
             footer={<Space><Button onClick={copy}>Copy email</Button><Button loading={busy} onClick={save}>Save to Document Control</Button><Button type="primary" loading={busy} onClick={download}>Download PDF</Button></Space>}>
        {data ? (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 8 }}
                   message="A draft: nothing is sent from BidLight. Copy the email, edit it, and send it yourself."
                   description={open_ ? `${open_} open decision(s) are listed for the meeting.` : undefined} />
            <Input value={data.email.subject} readOnly style={{ marginBottom: 6 }} />
            <Input.TextArea value={data.email.body} readOnly autoSize={{ minRows: 12, maxRows: 24 }} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
